import type {
  RoomThemeAccess,
  RoomThemeColors,
  RoomThemeRuntime,
  RoomThemeState,
} from "./types";

export const ROOM_THEME_ACCESS: readonly RoomThemeAccess[] = [
  "free",
  "premium",
  "unlockable",
] as const;

export const ROOM_THEME_STATES: readonly RoomThemeState[] = [
  "draft",
  "published",
  "archived",
] as const;

export const DEFAULT_ROOM_THEME_ID = "theme-classic-ivory";
export const DEFAULT_ROOM_THEME_NAME = "Classic Ivory";

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

export function createDefaultRoomThemeColors(): RoomThemeColors {
  return {
    wall: "#f0ebe3",
    floorTop: "#f4f4f4",
    floorSide: "#d8d8d8",
    porchMarker: "#c7c7c7",
  };
}

export function createDefaultRoomThemeRuntime(): RoomThemeRuntime {
  return {
    id: DEFAULT_ROOM_THEME_ID,
    name: DEFAULT_ROOM_THEME_NAME,
    access: "free",
    ...createDefaultRoomThemeColors(),
  };
}

export function normalizeHexColor(value: unknown, fallback: string): string {
  if (typeof value !== "string") {
    return fallback;
  }
  const trimmed = value.trim();
  if (!HEX_COLOR_RE.test(trimmed)) {
    return fallback;
  }
  return trimmed.toLowerCase();
}

export function validateThemeColors(colors: RoomThemeColors): string | null {
  if (!HEX_COLOR_RE.test(colors.wall)) {
    return "wall color must be in #RRGGBB format.";
  }
  if (!HEX_COLOR_RE.test(colors.floorTop)) {
    return "floorTop color must be in #RRGGBB format.";
  }
  if (!HEX_COLOR_RE.test(colors.floorSide)) {
    return "floorSide color must be in #RRGGBB format.";
  }
  if (!HEX_COLOR_RE.test(colors.porchMarker)) {
    return "porchMarker color must be in #RRGGBB format.";
  }
  return null;
}

export interface RoomThemeSeedEntry {
  id: string;
  name: string;
  slug: string;
  state: RoomThemeState;
  access: RoomThemeAccess;
  colors: RoomThemeColors;
}

export function defaultRoomThemeSeedEntries(): RoomThemeSeedEntry[] {
  return [
    {
      id: DEFAULT_ROOM_THEME_ID,
      name: DEFAULT_ROOM_THEME_NAME,
      slug: "classic-ivory",
      state: "published",
      access: "free",
      colors: createDefaultRoomThemeColors(),
    },
    {
      id: "theme-studio-slate",
      name: "Azure Breeze",
      slug: "azure-breeze",
      state: "published",
      access: "free",
      colors: {
        wall: "#8db9ff",
        floorTop: "#b9d7ff",
        floorSide: "#6d9ee8",
        porchMarker: "#5e8cd4",
      },
    },
    {
      id: "theme-lobby-mint",
      name: "Lobby Mint",
      slug: "lobby-mint",
      state: "published",
      access: "unlockable",
      colors: {
        wall: "#7fdb9f",
        floorTop: "#b9f2cd",
        floorSide: "#58b67a",
        porchMarker: "#4aa46d",
      },
    },
    {
      id: "theme-peach-sunset",
      name: "Peach Sunset",
      slug: "peach-sunset",
      state: "published",
      access: "unlockable",
      colors: {
        wall: "#ffb184",
        floorTop: "#ffd5bb",
        floorSide: "#e38a5f",
        porchMarker: "#cf7449",
      },
    },
    {
      id: "theme-midnight-club",
      name: "Midnight Club",
      slug: "midnight-club",
      state: "published",
      access: "premium",
      colors: {
        wall: "#7d7cff",
        floorTop: "#a4a2ff",
        floorSide: "#5652c9",
        porchMarker: "#4743ab",
      },
    },
  ];
}
